import { failureFromCode, type AskFailure } from './ask-errors'
import type { AskEvent, Citation, KnowledgeAnswerOutcome } from './portal-types'

/**
 * One answer, folded from the stream's events (K1 contract, "SSE events").
 *
 * - `text` deltas are the model's RAW output and only fill the screen while it
 *   streams; `done.content` is the server-sanitised answer and replaces them.
 * - The sources an answer shows are `done.citations` (the ones it cites). The
 *   `sources` event lists every passage handed to the model, cited or not, so
 *   it is never shown as the answer's sources.
 * - A failure drops whatever text had streamed: it was never sanitised.
 */
export interface AskTurn {
  id: string
  status: 'streaming' | 'done' | 'failed'
  text: string
  citations: Citation[]
  outcome: KnowledgeAnswerOutcome | null
  failure: AskFailure | null
  messageId: string | null
}

export function startTurn(id: string): AskTurn {
  return { id, status: 'streaming', text: '', citations: [], outcome: null, failure: null, messageId: null }
}

export function failTurn(turn: AskTurn, failure: AskFailure): AskTurn {
  return { ...turn, status: 'failed', failure, text: '', citations: [] }
}

/** The state an outcome that is not an answer is shown as. */
function outcomeFailure(outcome: KnowledgeAnswerOutcome): AskFailure | null {
  if (outcome === 'limited') return 'limit'
  if (outcome === 'error') return 'unavailable'
  return null
}

/** A finished answer from a stored message or a `done` event. */
export function finishedTurn(
  id: string,
  outcome: KnowledgeAnswerOutcome | null,
  content: string,
  citations: Citation[],
  messageId: string | null = null,
): AskTurn {
  const resolved = outcome ?? 'answered'
  const failure = outcomeFailure(resolved)
  if (failure) return { ...failTurn(startTurn(id), failure), outcome: resolved, messageId }
  return { id, status: 'done', text: content, citations, outcome: resolved, failure: null, messageId }
}

export function applyAskEvent(turn: AskTurn, event: AskEvent): AskTurn {
  if (turn.status !== 'streaming') return turn
  switch (event.type) {
    case 'text':
      return { ...turn, text: turn.text + event.delta }
    case 'done':
      return finishedTurn(turn.id, event.outcome, event.content, event.citations, event.messageId)
    case 'error':
      return failTurn(turn, failureFromCode(event.errorCode))
    default:
      // `conversation` belongs to the conversation; `sources` are not citations.
      return turn
  }
}

/** The stream closed: an answer still streaming never got its `done`. */
export function endTurn(turn: AskTurn): AskTurn {
  return turn.status === 'streaming' ? failTurn(turn, 'unavailable') : turn
}
